// src/hooks/useRecommendedActions.js
// ============================================================================
// RECOMMENDED ACTIONS HOOK
// ============================================================================
// Manages workflow-assistant recommendations: in-memory state with optional
// persistence to the Supabase recommended_actions table.
//
// Works fully client-side even if the DB table doesn't exist yet.
// Pattern: same field-locked approach as useSupabase.js hooks.
// ============================================================================
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { calculateDueDate, getInvoiceDisplayStatus } from '../utils/emailTemplates';

// ── Field mapping (snake_case DB → camelCase JS) ────────────────────────────

function mapRow(r) {
  return {
    id:          r.id,
    projectId:   r.project_id,
    stageKey:    r.stage_key    ?? null,
    invoiceId:   r.invoice_id   ?? null,
    triggerType: r.trigger_type,
    actionType:  r.action_type,
    title:       r.title,
    description: r.description  ?? '',
    status:      r.status,
    metadata:    r.metadata     ?? {},
    createdAt:   r.created_at,
    updatedAt:   r.updated_at,
    createdBy:   r.created_by   ?? null,
  };
}

// ── Action type definitions ─────────────────────────────────────────────────

const ACTION_DEFS = {
  create_proforma_invoice: {
    icon: 'FileText',
    color: 'var(--brio-gold, #9F902A)',
    category: 'invoice',
  },
  create_tax_invoice: {
    icon: 'FileText',
    color: 'var(--brio-gold, #9F902A)',
    category: 'invoice',
  },
  generate_invoice_pdf: {
    icon: 'Download',
    color: 'var(--brio-gold, #9F902A)',
    category: 'invoice',
  },
  draft_project_initiated_email: {
    icon: 'Mail',
    color: 'var(--brio-teal, #689581)',
    category: 'email',
  },
  draft_stage_initiation_email: {
    icon: 'Mail',
    color: 'var(--brio-teal, #689581)',
    category: 'email',
  },
  draft_stage_closure_email: {
    icon: 'Mail',
    color: 'var(--brio-teal, #689581)',
    category: 'email',
  },
  draft_tax_invoice_email: {
    icon: 'Mail',
    color: 'var(--brio-gold, #9F902A)',
    category: 'email',
  },
  draft_timeline_change_email: {
    icon: 'Mail',
    color: 'var(--brio-yellow, #ECD288)',
    category: 'email',
  },
  draft_payment_reminder_email: {
    icon: 'Bell',
    color: 'var(--brio-red, #934948)',
    category: 'reminder',
  },
  draft_stage_completion_approval_email: {
    icon: 'Mail',
    color: 'var(--brio-teal, #689581)',
    category: 'email',
  },
  draft_payment_confirmed_email: {
    icon: 'Mail',
    color: 'var(--brio-teal, #689581)',
    category: 'email',
  },
  draft_planned_date_revised_email: {
    icon: 'Mail',
    color: 'var(--brio-yellow, #ECD288)',
    category: 'email',
  },
  notify_admin: {
    icon: 'AlertTriangle',
    color: 'var(--brio-yellow, #ECD288)',
    category: 'admin',
  },
  review_missing_payment_terms: {
    icon: 'AlertTriangle',
    color: 'var(--brio-orange, #DB6B5D)',
    category: 'review',
  },
};

// ── Trigger → recommended actions mapping ───────────────────────────────────

const TRIGGER_ACTIONS = {
  // ── Timeline date triggers ─────────────────────────────────────────
  // Stage closure (actualDate) → native mail: stage completion approval request
  stage_closure: [
    {
      actionType: 'draft_stage_completion_approval_email',
      title: (meta) => `Draft stage completion approval email — ${meta.stageName || 'stage'}`,
      description: (meta) => `Stage ${meta.stageName || ''} completed. Send approval request to client via mail.`,
      routing: 'native_mail',
    },
  ],
  // Proforma invoice date → RADAR notification for admin
  proforma_invoice_created: [
    {
      actionType: 'create_proforma_invoice',
      title: (meta) => `RADAR: Send proforma invoice — ${meta.stageName || 'stage'}`,
      description: () => 'Proforma invoice date set. Admin will receive a RADAR notification to send the proforma invoice.',
      routing: 'radar',
    },
  ],
  // Invoice date → RADAR notification for admin
  invoice_sent: [
    {
      actionType: 'create_tax_invoice',
      title: (meta) => `RADAR: Send tax invoice — ${meta.stageName || 'stage'}`,
      description: () => 'Invoice date set. Admin will receive a RADAR notification to send the tax invoice.',
      routing: 'radar',
    },
  ],
  // Timeline change (targetDate) → native mail: planned date revised
  timeline_change: [
    {
      actionType: 'draft_planned_date_revised_email',
      title: (meta) => `Draft planned date revised email — ${meta.stageName || 'stage'}`,
      description: (meta) => {
        const prev = meta.previousDate || '?';
        const rev = meta.revisedDate || '?';
        return `Planned date changed from ${prev} to ${rev}. Notify the client via mail.`;
      },
      routing: 'native_mail',
    },
  ],
  // ── Payment triggers → native mail ─────────────────────────────────
  advance_paid: [
    {
      actionType: 'draft_payment_confirmed_email',
      title: (meta) => `Draft payment confirmed email — ${meta.projectName || 'project'}`,
      description: () => 'Advance payment confirmed. Notify the client that the project has been initiated.',
      routing: 'native_mail',
    },
  ],
  invoice_paid: [
    {
      actionType: 'draft_payment_confirmed_email',
      title: (meta) => `Draft payment confirmed email — ${meta.stageName || 'stage'}`,
      description: (meta) => `${meta.stageName || 'Stage'} payment confirmed. Notify the client that the next stage will commence.`,
      routing: 'native_mail',
    },
  ],
  // ── Passive scan triggers (auto-detected, not from date changes) ──
  payment_overdue: [
    {
      actionType: 'draft_payment_reminder_email',
      title: (meta) => `Send payment reminder — Invoice #${meta.invoiceNumber || '?'}`,
      description: (meta) => {
        const days = meta.daysOverdue || 0;
        return `Invoice is ${days} day${days === 1 ? '' : 's'} overdue. Send a payment reminder.`;
      },
    },
  ],
  missing_payment_terms: [
    {
      actionType: 'review_missing_payment_terms',
      title: () => 'Review missing payment terms',
      description: () => 'This client has no payment terms configured. Set payment terms before invoicing.',
    },
  ],
};

// ── Local ID counter for in-memory actions ──────────────────────────────────
let localIdCounter = 1;

// ── Hook ─────────────────────────────────────────────────────────────────────

export default function useRecommendedActions(projectId) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  // Track whether DB table is available; once we know it's not, skip DB calls
  const dbAvailable = useRef(null); // null = unknown, true/false after first attempt
  // Ref mirror of actions for dedup checks without stale closures
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  // ── Probe table existence (runs once) ──
  const probeDb = useCallback(async () => {
    if (dbAvailable.current !== null) return dbAvailable.current;
    try {
      const { error } = await supabase
        .from('recommended_actions')
        .select('id')
        .limit(1);
      // Table exists if no error, or if there's a permission error (table exists but RLS blocks)
      const exists = !error || error.code === 'PGRST301';
      dbAvailable.current = exists;
      return exists;
    } catch {
      dbAvailable.current = false;
      return false;
    }
  }, []);

  // ── Fetch actions for project ──
  const fetchActions = useCallback(async () => {
    if (!projectId) { setActions([]); return; }
    setLoading(true);
    const canUseDb = await probeDb();
    if (canUseDb) {
      try {
        const { data, error } = await supabase
          .from('recommended_actions')
          .select('*')
          .eq('project_id', projectId)
          .order('created_at', { ascending: false });
        if (!error) {
          const dbActions = (data || []).map(mapRow);
          const dbIds = new Set(dbActions.map(a => a.id));
          // Merge: keep any in-memory actions not yet returned by DB
          // (guards against race where fetchActions SELECT precedes a concurrent createAction INSERT)
          const inMemOnly = actionsRef.current.filter(a => !dbIds.has(a.id));
          const merged = [...inMemOnly, ...dbActions];
          actionsRef.current = merged;
          setActions(merged);
          setLoading(false);
          return;
        }
        // Table gone or error → fall back to in-memory
        console.warn('[useRecommendedActions] DB fetch failed, using in-memory:', error.message);
        dbAvailable.current = false;
      } catch (err) {
        console.warn('[useRecommendedActions] DB fetch exception, using in-memory:', err.message);
        dbAvailable.current = false;
      }
    }
    // In-memory mode: keep existing actions (don't reset), just stop loading
    setLoading(false);
  }, [projectId, probeDb]);

  useEffect(() => { fetchActions(); }, [fetchActions]);

  // ── Create action (with client-side deduplication + optional DB persist) ──
  const createAction = useCallback(async ({ stageKey, invoiceId, triggerType, actionType, title, description, metadata }) => {
    if (!projectId) return { success: false, error: 'No project' };

    // Client-side deduplication using ref (avoids stale closure in sequential calls)
    const isDup = actionsRef.current.some(a =>
      a.status === 'open' &&
      (a.stageKey || '') === (stageKey || '') &&
      a.triggerType === triggerType &&
      a.actionType === actionType
    );
    if (isDup) return { success: true, duplicate: true };

    // Try DB persist first
    if (dbAvailable.current) {
      try {
        // Check for existing open action in DB (select full row so we can return it)
        const { data: existing } = await supabase
          .from('recommended_actions')
          .select('*')
          .eq('project_id', projectId)
          .eq('trigger_type', triggerType)
          .eq('action_type', actionType)
          .eq('status', 'open')
          .maybeSingle();

        if (existing) {
          const mapped = mapRow(existing);
          // Add to in-memory ref if not already present (handles race condition on navigation)
          if (!actionsRef.current.some(a => a.id === mapped.id)) {
            actionsRef.current = [mapped, ...actionsRef.current];
            setActions(actionsRef.current);
          }
          return { success: true, duplicate: true, data: mapped };
        }

        const insertPayload = {
          project_id:   projectId,
          stage_key:    stageKey || null,
          invoice_id:   invoiceId || null,
          trigger_type: triggerType,
          action_type:  actionType,
          title,
          description:  description || '',
          status:       'open',
          metadata:     metadata || {},
        };

        const { data, error } = await supabase
          .from('recommended_actions')
          .insert(insertPayload)
          .select();

        if (!error && data && data.length > 0) {
          const mapped = mapRow(data[0]);
          actionsRef.current = [mapped, ...actionsRef.current];
          setActions(actionsRef.current);
          return { success: true, data: mapped };
        }
        if (error) {
          if (error.code === '23505') {
            // Unique constraint — action exists but may be completed/dismissed.
            // Fetch it (any status) and reopen so it surfaces in the bottom panel.
            try {
              const { data: found } = await supabase
                .from('recommended_actions')
                .select('*')
                .eq('project_id', projectId)
                .eq('trigger_type', triggerType)
                .eq('action_type', actionType)
                .maybeSingle();
              if (found) {
                let mapped = mapRow(found);
                if (mapped.status !== 'open') {
                  await supabase.from('recommended_actions').update({ status: 'open' }).eq('id', found.id);
                  mapped = { ...mapped, status: 'open' };
                }
                const idx = actionsRef.current.findIndex(a => a.id === mapped.id);
                if (idx >= 0) {
                  actionsRef.current = actionsRef.current.map((a, i) => i === idx ? mapped : a);
                } else {
                  actionsRef.current = [mapped, ...actionsRef.current];
                }
                setActions(actionsRef.current);
                return { success: true, data: mapped };
              }
            } catch { /* ignore */ }
            return { success: true, duplicate: true };
          }
          console.warn('[useRecommendedActions] DB insert failed, using in-memory:', error.message);
        }
      } catch (err) {
        console.warn('[useRecommendedActions] DB insert exception, using in-memory:', err.message);
      }
    }

    // In-memory fallback: create a local action
    const localAction = {
      id:          `local_${localIdCounter++}`,
      projectId:   projectId,
      stageKey:    stageKey || null,
      invoiceId:   invoiceId || null,
      triggerType,
      actionType,
      title,
      description: description || '',
      status:      'open',
      metadata:    metadata || {},
      createdAt:   new Date().toISOString(),
      updatedAt:   new Date().toISOString(),
      createdBy:   null,
    };
    actionsRef.current = [localAction, ...actionsRef.current];
    setActions(actionsRef.current);
    return { success: true, data: localAction };
  }, [projectId]);

  // ── Update action status ──
  const updateStatus = useCallback(async (actionId, newStatus) => {
    // Update in-memory state first (always)
    setActions(prev => prev.map(a =>
      a.id === actionId ? { ...a, status: newStatus, updatedAt: new Date().toISOString() } : a
    ));

    // Try DB update (non-blocking, only for real DB IDs)
    if (dbAvailable.current && typeof actionId === 'number') {
      try {
        const { error } = await supabase
          .from('recommended_actions')
          .update({ status: newStatus })
          .eq('id', actionId);
        if (error) {
          console.warn('[useRecommendedActions] DB update failed:', error.message);
        }
      } catch (err) {
        console.warn('[useRecommendedActions] DB update exception:', err.message);
      }
    }
    return { success: true };
  }, []);

  const dismiss  = useCallback((id) => updateStatus(id, 'dismissed'), [updateStatus]);
  const complete = useCallback((id) => updateStatus(id, 'completed'), [updateStatus]);
  const reopen   = useCallback((id) => updateStatus(id, 'open'), [updateStatus]);

  // ── Generate recommended actions from a trigger event ──
  const generateFromTrigger = useCallback(async (triggerType, meta = {}) => {
    const defs = TRIGGER_ACTIONS[triggerType];
    if (!defs) return [];

    const created = [];
    for (const def of defs) {
      // Merge routing from action definition into metadata
      const actionMeta = { ...meta, routing: def.routing || meta.routing || null };
      const result = await createAction({
        stageKey:    meta.stageKey || null,
        invoiceId:   meta.invoiceId || null,
        triggerType,
        actionType:  def.actionType,
        title:       def.title(meta),
        description: def.description(meta),
        metadata:    actionMeta,
      });
      if (result.success && result.data) {
        created.push(result.data);
      } else if (result.success && result.duplicate) {
        // Use action returned from DB duplicate check first (handles navigation race condition
        // where actionsRef.current may not yet be populated from fetchActions).
        // Fall back to in-memory lookup for client-side duplicate path.
        const existing = result.data || actionsRef.current.find(a =>
          a.status === 'open' &&
          (a.stageKey || '') === (meta.stageKey || '') &&
          a.triggerType === triggerType &&
          a.actionType === def.actionType
        );
        if (existing) created.push(existing);
      }
    }
    return created;
  }, [createAction]);

  // ── Scan project for overdue invoices and missing payment terms ──
  const scanProjectForActions = useCallback(async (project, invoices, client) => {
    if (!project || !invoices) return;

    // Check for missing payment terms
    if (client && !client.paymentTermsDays) {
      const hasInvoices = invoices.some(inv => inv.status === 'issued');
      if (hasInvoices) {
        await generateFromTrigger('missing_payment_terms', {
          stageKey: null,
          clientName: client.name,
        });
      }
    }

    // Check for overdue invoices
    for (const inv of invoices) {
      if (inv.status !== 'issued') continue;
      const { dueDate } = calculateDueDate(inv.invoiceDate, client?.paymentTermsDays);
      if (!dueDate) continue;
      const displayStatus = getInvoiceDisplayStatus(inv, dueDate);
      if (displayStatus === 'overdue') {
        const daysOverdue = Math.max(0, Math.floor((new Date() - new Date(dueDate)) / 86400000));
        await generateFromTrigger('payment_overdue', {
          stageKey: inv.stageKey,
          invoiceId: inv.id,
          invoiceNumber: inv.invoiceNumber,
          daysOverdue,
        });
      }
    }
  }, [generateFromTrigger]);

  // ── Derived: filtered by status ──
  const openActions = actions.filter(a => a.status === 'open');
  const dismissedActions = actions.filter(a => a.status === 'dismissed');
  const completedActions = actions.filter(a => a.status === 'completed');

  return {
    actions,
    openActions,
    dismissedActions,
    completedActions,
    loading,
    fetchActions,
    createAction,
    dismiss,
    complete,
    reopen,
    generateFromTrigger,
    scanProjectForActions,
    ACTION_DEFS,
  };
}

// Export for use in trigger integration
export { ACTION_DEFS, TRIGGER_ACTIONS };
